import { Store } from '@ngrx/store';
import { Injectable } from '@angular/core';
import { map, switchMap, withLatestFrom } from 'rxjs/operators';
import { from } from 'rxjs';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { AppState, DeleteCart, selectCurrentTrip } from '../index';
import {
  CartActionTypes,
  CartUpdated,
  EmptyCheckout,
  PaxSelectedChangedForSpecialItems,
  PaxSelectedForSpecialItems,
} from './cart.actions';
import { selectCart } from './cart.selector';
import { CartService } from 'src/app/services/api/cart/cart.service';
import { Router } from '@angular/router';
import { Cart, Checkout } from './cart.model';
import { Logging } from '../../services/logging/logging.service';
import { ApplinkService } from '../../services/hardware/applink.service';
import { AppRoutes } from 'src/app/app-routing.module';
import { DocumentService } from '../../services/api/document/document.service';

@Injectable()
export class CartEffects {
  constructor(
    private actions$: Actions,
    private router: Router,
    public cartService: CartService,
    private store: Store<AppState>,
    private logging: Logging,
    private applinkService: ApplinkService,
    private documentService: DocumentService
  ) {}

  @Effect({ dispatch: false })
  updateCartAndGoToNextStep$ = this.actions$.pipe(
    ofType(CartActionTypes.UpdateCartAndGoToNextStep),
    withLatestFrom(this.store.select(selectCart), this.store.select(selectCurrentTrip)),
    switchMap(([_, cart, trip]) => from(this.cartService.updateCart(cart, trip.id))),
    map((response) => {
      if (!response || !response.results) {
        return false;
      }
      const updatedCart: Cart = Cart.deserializeFromJson(response.results[0]);

      this.store.dispatch(new CartUpdated({ cart: updatedCart }));

      if (updatedCart && updatedCart.grandTotal > 0) {
        this.router.navigate([AppRoutes.PAYMENT]);
      } else {
        this.store.dispatch(new EmptyCheckout());
        this.router.navigate([AppRoutes.HAZMAT_PROHIBITED]);
      }

      return updatedCart;
    })
  );

  @Effect({ dispatch: false })
  emptyCheckout$ = this.actions$.pipe(
    ofType(CartActionTypes.EmptyCheckout),
    withLatestFrom(this.store.select(selectCart), this.store.select(selectCurrentTrip)),
    switchMap(([_, cart, trip]) =>
      from(
        this.cartService.checkoutCart(
          this.getEmptyCheckout(cart.confirmationCode),
          cart.id,
          trip.id,
          this.applinkService.getKioskId()
        )
      )
    ),
    map(() => {
      // We need to delete the cart even if its a zero dollar cart
      this.logging.infoApiCheckoutSuccessful();
      this.store.dispatch(new DeleteCart());
      return true;
    })
  );

  @Effect({ dispatch: false })
  deleteCart$ = this.actions$.pipe(
    ofType(CartActionTypes.DeleteCart),
    withLatestFrom(this.store.select(selectCart), this.store.select(selectCurrentTrip)),
    map(([_, cart, trip]) => {
      if (cart.id) {
        from(this.cartService.deleteCart(trip, cart)).pipe(
          map((cartResult) => {
            return true;
            /*For now we can ignore the result since it will be an empty body coming back*/
          })
        );
      }
    })
  );

  @Effect({ dispatch: false })
  updateCartRequested$ = this.actions$.pipe(
    ofType(CartActionTypes.UpdateCartRequested),
    withLatestFrom(this.store.select(selectCart), this.store.select(selectCurrentTrip)),
    switchMap(([_, cart, trip]) => from(this.cartService.updateCart(cart, trip.id))),
    map((response) => {
      if (!response || !response.results) {
        return false;
      }
      const updatedCart: Cart = Cart.deserializeFromJson(response.results[0]);

      this.store.dispatch(new CartUpdated({ cart: updatedCart }));

      return updatedCart;
    })
  );

  @Effect({ dispatch: false })
  selectPaxForSpecialItems$ = this.actions$.pipe(
    ofType(CartActionTypes.PaxSelectedForSpecialItems),
    withLatestFrom(this.store.select(selectCurrentTrip)),
    map(([action, trip]) => {
      const { passengerId } = (action as PaxSelectedForSpecialItems).payload;

      const passenger = trip.passengers.filter((pax) => pax.id === passengerId)[0];
      this.store.dispatch(new PaxSelectedChangedForSpecialItems({ passenger }));
      this.router.navigate([AppRoutes.SPECIAL_ITEMS]);
    })
  );

  getEmptyCheckout(confirmationCode): Checkout {
    const pointOfSale = this.documentService.getPointOfSale();
    return {
      checkoutTransactionId: '001',
      confirmationCode,
      pointOfSale,
      payments: [],
    };
  }
}
